import { inr, STATUS_LABEL, FREQ_LABEL, taxLabel } from "./fq";
import { AlertTriangle, Info } from "lucide-react";

const amountCell = (l) => {
  if (l.status === "INCLUDED") return "Included";
  if (l.status === "KNOWN") return inr(l.amount);
  if (l.status === "REFERENCE_PENDING" && l.unit_price) return `Ref. ${inr(l.unit_price)} · not in subtotal`;
  if (l.amount !== null && l.amount !== undefined) return `${inr(l.amount)} · ${STATUS_LABEL[l.status]?.toLowerCase()}`;
  return STATUS_LABEL[l.status] || l.status;
};

const Section = ({ letter, title, children, testid }) => (
  <section className="fq-section" data-testid={testid}>
    <div className="flex items-baseline gap-3 mb-3">
      <span className="fq-letter">{letter}</span>
      <h3 className="font-display text-lg text-[#3A0A06]">{title}</h3>
    </div>
    {children}
  </section>
);

const Lines = ({ lines, cols }) => (
  <div className="overflow-x-auto">
    <table className="fq-table">
      <thead><tr>{cols.map((c) => <th key={c.k}>{c.h}</th>)}</tr></thead>
      <tbody>
        {lines.map((l) => (
          <tr key={l.id} data-testid={`quote-line-${l.id}`}>
            {cols.map((c) => <td key={c.k} className={c.k === "amount" ? "font-semibold whitespace-nowrap" : ""}>{c.r(l)}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Subtotal = ({ sec, label, testid }) => (
  <div className="fq-subtotal" data-testid={testid}>
    <span>{label} <span className="text-xs font-normal opacity-70">(before applicable taxes)</span></span>
    <span className="font-display text-xl">{inr(sec.known_subtotal)}</span>
    {sec.has_pending && <p className="w-full text-xs text-[#8A4B0F]">Known charges subtotal — additional items awaiting confirmation</p>}
  </div>
);

export const QuoteSectionsAB = ({ s }) => (
  <>
    <Section letter="A" title="One-time franchise and onboarding fees" testid="quote-section-a">
      <Lines lines={s.A.lines} cols={[
        { k: "l", h: "Item", r: (l) => <><div className="font-medium">{l.label}</div><div className="text-xs opacity-70">{l.note}</div></> },
        { k: "q", h: "Qty", r: (l) => l.qty || "—" },
        { k: "amount", h: "Amount", r: amountCell },
        { k: "t", h: "Tax", r: (l) => taxLabel(l.tax) },
      ]} />
      <Subtotal sec={s.A} label="Known one-time subtotal" testid="quote-subtotal-a" />
    </Section>
    <Section letter="B" title="Fixed monthly service charges" testid="quote-section-b">
      <Lines lines={s.B.lines} cols={[
        { k: "l", h: "Item", r: (l) => <div className="font-medium">{l.label}</div> },
        { k: "q", h: "Qty", r: (l) => l.qty || "—" },
        { k: "rate", h: "Rate", r: (l) => (l.unit_price != null ? inr(l.unit_price) : "—") },
        { k: "amount", h: "Per month", r: amountCell },
        { k: "t", h: "Tax", r: (l) => taxLabel(l.tax) },
        { k: "p", h: "By", r: (l) => l.performer || "—" },
      ]} />
      <Subtotal sec={s.B} label="Known fixed monthly subtotal" testid="quote-subtotal-b" />
    </Section>
  </>
);

export const QuoteSectionsCD = ({ s }) => (
  <>
    <Section letter="C" title="Variable terms and actual-cost services" testid="quote-section-c">
      {s.C.lines.length ? (
        <Lines lines={s.C.lines} cols={[
          { k: "l", h: "Item", r: (l) => <div className="font-medium">{l.label}</div> },
          { k: "f", h: "Frequency", r: (l) => FREQ_LABEL[l.frequency] || l.frequency || "—" },
          { k: "amount", h: "Amount / status", r: amountCell },
          { k: "n", h: "Basis", r: (l) => <span className="text-xs">{l.note}</span> },
        ]} />
      ) : <p className="text-sm opacity-70">No variable items for this selection.</p>}
      <p className="text-xs mt-2 opacity-70">Variable amounts are not added to any subtotal.</p>
    </Section>
    <Section letter="D" title="Six-month staffing service fee" testid="quote-section-d">
      {s.D ? (
        <>
          <Lines lines={s.D.roles.map((r, i) => ({ ...r, id: `role-${i}` }))} cols={[
            { k: "r", h: "Role", r: (r) => r.role },
            { k: "q", h: "Qty", r: (r) => r.qty },
            { k: "s", h: "Monthly salary", r: (r) => (r.monthly_salary != null ? inr(r.monthly_salary) : "Pending") },
            { k: "st", h: "Status", r: (r) => (r.salary_status === "AGREED" ? "Agreed" : "Illustrative") },
            { k: "amount", h: "6-month fee", r: (r) => (r.fee != null ? inr(r.fee) : "Pending") },
          ]} />
          <div className="fq-subtotal" data-testid="quote-subtotal-d">
            <span>Sourcing fee per six-month period</span>
            <span className="font-display text-xl">{inr(s.D.fee)}</span>
            <p className="w-full text-xs opacity-80">Separate from wages: approx. {inr(s.D.monthly_wages_reference)} payroll every month, paid by the employer. {s.D.note}</p>
          </div>
        </>
      ) : <p className="text-sm opacity-70">Staff sourcing not selected.</p>}
    </Section>
  </>
);

export const QuoteSectionE = ({ e, formatLabel }) => (
  <Section letter="E" title={`Setup & funding reference — ${formatLabel}`} testid="quote-section-e">
    <div className="fq-callout mb-3"><Info className="w-4 h-4 shrink-0" /> CAPEX, deposits, opening costs, inventories, payroll and working capital are NOT part of subtotals A–D. Reference estimates only.</div>
    <details className="fq-details" data-testid="quote-capex-toggle">
      <summary>Show itemised reference ({inr(e.priced_capex_subtotal)} priced CAPEX)</summary>
      {Object.entries(e.groups).map(([g, items]) => (
        <div key={g} className="mt-3">
          <div className="text-xs uppercase tracking-widest text-[#8A4B0F] mb-1">{g.replace("_", " ")}</div>
          {items.map((it) => (
            <div key={it.id} className="flex justify-between text-sm py-1 border-b border-[#EADFCC]">
              <span>{it.label}</span>
              <span className="font-medium">{it.amount != null ? inr(it.amount) : it.status === "ACTUALS" ? "Actuals" : "Not priced"}</span>
            </div>
          ))}
        </div>
      ))}
      <div className="flex justify-between text-sm py-2 font-semibold"><span>Priced CAPEX subtotal</span><span data-testid="quote-capex-subtotal">{inr(e.priced_capex_subtotal)}</span></div>
      <div className="flex justify-between text-sm py-1"><span>Original reference schedule (incl. chart franchise fee {inr(e.chart_franchise_fee)})</span><span>{inr(e.original_chart_total)}</span></div>
      <ul className="text-xs mt-2 space-y-1 opacity-80 list-disc pl-4">{e.notes.map((n) => <li key={n}>{n}</li>)}</ul>
    </details>
  </Section>
);

export const QuoteFlags = ({ result }) => (
  <div className="space-y-2">
    {result.provisional_reasons?.length > 0 && (
      <div className="fq-callout warn" data-testid="quote-provisional-banner">
        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
        <div><b>Indicative & provisional.</b><ul className="list-disc pl-4 mt-1 text-xs space-y-0.5">{result.provisional_reasons.map((r) => <li key={r}>{r}</li>)}</ul></div>
      </div>
    )}
    {result.flags?.map((f) => (
      <div key={f.code} className="fq-callout danger" data-testid={`quote-flag-${f.code}`}><AlertTriangle className="w-4 h-4 shrink-0" />{f.message}</div>
    ))}
  </div>
);

export default function QuoteView({ result }) {
  const s = result.sections;
  return (
    <div className="space-y-6" data-testid="quote-view">
      <QuoteFlags result={result} />
      <QuoteSectionsAB s={s} />
      <QuoteSectionsCD s={s} />
      <QuoteSectionE e={s.E} formatLabel={result.format.label} />
      <div className="grid md:grid-cols-2 gap-4 text-xs">
        <div><h4 className="font-semibold mb-1">Exclusions</h4><ul className="list-disc pl-4 space-y-1">{result.exclusions.map((x) => <li key={x}>{x}</li>)}</ul></div>
        <div><h4 className="font-semibold mb-1">Billing policy</h4><ul className="list-disc pl-4 space-y-1">{result.billing_policy.map((x) => <li key={x}>{x}</li>)}</ul></div>
      </div>
      <p className="text-xs opacity-70">{result.tax_note}</p>
    </div>
  );
}
